import React, { useState, useEffect } from 'react';
import './BackToTop.css';

const BackToTop = () => {
    const [showScrollTop, setShowScrollTop] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone: '',
        country: '',
        helpDescription: '',
        contactMethod: 'Email',
        bestTime: 'Morning'
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);

    // Scroll to top functionality
    useEffect(() => {
        const handleScroll = () => {
            if (window.pageYOffset > 300) {
                setShowScrollTop(true);
            } else {
                setShowScrollTop(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    // Form handling
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.fullName.trim()) {
            newErrors.fullName = 'Full Name is required';
        }
        
        if (!formData.email.trim()) {
            newErrors.email = 'Email Address is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }
        
        if (!formData.country.trim()) {
            newErrors.country = 'Country of Residence is required';
        }
        
        if (!formData.helpDescription.trim()) {
            newErrors.helpDescription = 'Please describe how we can help you';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            // Send to your PHP backend
            const response = await fetch('https://thenriadvisors.com/api/send-email.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    fullName: formData.fullName,
                    email: formData.email,
                    phone: formData.phone || 'Not provided',
                    country: formData.country,
                    helpDescription: formData.helpDescription,
                    contactMethod: formData.contactMethod,
                    bestTime: formData.bestTime
                })
            });

            const result = await response.json();

            if (result.success) {
                setSubmitSuccess(true);
                setFormData({
                    fullName: '',
                    email: '',
                    phone: '',
                    country: '',
                    helpDescription: '',
                    contactMethod: 'Email',
                    bestTime: 'Morning'
                });
                setErrors({});

                // Close form after 3 seconds
                setTimeout(() => {
                    setShowForm(false);
                    setSubmitSuccess(false);
                }, 3000);
            } else {
                setErrors({ submit: result.message || 'Failed to send message. Please try again.' });
            }

        } catch (error) {
            console.error('Error sending email:', error);
            setErrors({ submit: 'Failed to send message. Please try again.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const closeForm = () => {
        setShowForm(false);
        setSubmitSuccess(false);
        setErrors({});
        setFormData({
            fullName: '',
            email: '',
            phone: '',
            country: '',
            helpDescription: '',
            contactMethod: 'Email',
            bestTime: 'Morning'
        });
    };

    return (
        <>
            {/* Inquiry Button */}
            <div className={`inquiry-button ${showScrollTop ? 'show' : ''}`} onClick={() => setShowForm(true)}>
                <i className="fas fa-comments"></i>
                <span>Message</span>
            </div>

            {/* Back to Top Button */}
            {/* <div className={`scroll-to-top ${showScrollTop ? 'show' : ''}`} onClick={scrollToTop}>
                <i className="fas fa-arrow-up"></i>
            </div> */}

            {/* Inquiry Form Modal */}
            {showForm && (
                <div className="inquiry-modal-overlay" onClick={closeForm}>
                    <div className="inquiry-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="inquiry-modal-header">
                            <h3>Let's get started – How can we help you?</h3>
                            <button className="close-button" onClick={closeForm}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        {submitSuccess ? (
                            <div className="success-message">
                                <i className="fas fa-check-circle"></i>
                                <h4>Thank you for your inquiry!</h4>
                                <p>We've received your message and will get back to you within 24-48 hours.</p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="inquiry-form">
                                <div className="form-group">
                                    <label htmlFor="fullName">Full Name *</label>
                                    <input
                                        type="text"
                                        id="fullName"
                                        name="fullName"
                                        value={formData.fullName}
                                        onChange={handleInputChange}
                                        className={errors.fullName ? 'error' : ''}
                                        placeholder="Enter your full name"
                                    />
                                    {errors.fullName && <span className="error-message">{errors.fullName}</span>}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="email">Email Address *</label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className={errors.email ? 'error' : ''}
                                        placeholder="Enter your email address"
                                    />
                                    {errors.email && <span className="error-message">{errors.email}</span>}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="phone">Phone Number (Optional)</label>
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        placeholder="Enter your phone number"
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="country">Country of Residence *</label>
                                    <input
                                        type="text"
                                        id="country"
                                        name="country"
                                        value={formData.country}
                                        onChange={handleInputChange}
                                        className={errors.country ? 'error' : ''}
                                        placeholder="Enter your country of residence"
                                    />
                                    {errors.country && <span className="error-message">{errors.country}</span>}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="helpDescription">How Can We Help You? *</label>
                                    <textarea
                                        id="helpDescription"
                                        name="helpDescription"
                                        value={formData.helpDescription}
                                        onChange={handleInputChange}
                                        className={errors.helpDescription ? 'error' : ''}
                                        placeholder="Briefly tell us what you're looking for – execution of wills, property help, legal support, taxation, documentation, or something else."
                                        rows="4"
                                    />
                                    {errors.helpDescription && <span className="error-message">{errors.helpDescription}</span>}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="contactMethod">Preferred Method of Contact</label>
                                    <select
                                        id="contactMethod"
                                        name="contactMethod"
                                        value={formData.contactMethod}
                                        onChange={handleInputChange}
                                    >
                                        <option value="Email">Email</option>
                                        <option value="Phone">Phone</option>
                                        <option value="WhatsApp">WhatsApp</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="bestTime">Best Time to Reach You</label>
                                    <select
                                        id="bestTime"
                                        name="bestTime"
                                        value={formData.bestTime}
                                        onChange={handleInputChange}
                                    >
                                        <option value="Morning">Morning</option>
                                        <option value="Afternoon">Afternoon</option>
                                        <option value="Evening">Evening</option>
                                        <option value="Anytime">Anytime</option>
                                    </select>
                                </div>

                                {errors.submit && <div className="error-message">{errors.submit}</div>}

                                <div className="form-actions">
                                    {/* <button type="button" className="btn-secondary bg-white" onClick={closeForm}>
                                        Cancel
                                    </button> */}
                                    <button type="submit" className="btn btn-secondary  " disabled={isSubmitting}>
                                        {isSubmitting ? (
                                            <>
                                                <i className="fas fa-spinner fa-spin"></i>
                                                Sending...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-paper-plane"></i>
                                                Submit Inquiry
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default BackToTop;
